import React from 'react';
import { Settings, ChevronDown, ChevronRight, Eye, EyeOff, AlertTriangle, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * AdminCredentialsCard - Identifiants Admin WordPress (Amiraux uniquement)
 */
const AdminCredentialsCard = ({
  sites,
  showAdminCredentials,
  setShowAdminCredentials,
  adminCredentials,
  showAdminPasswords,
  setShowAdminPasswords,
  savingAdminCreds,
  onUpdateAdminCredential,
  onSaveAdminCredentials,
}) => {
  return (
    <Card className="relative bg-slate-950/40 backdrop-blur-xl border-purple-800/60">
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-900/30 transition-colors rounded-t-lg"
        onClick={() => setShowAdminCredentials(!showAdminCredentials)}
        data-testid="toggle-admin-credentials"
      >
        <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-purple-400 flex items-center justify-between">
          <span className="flex items-center">
            <Settings className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
            Identifiants Admin WordPress
            <span className="ml-2 text-[10px] bg-purple-500/20 px-2 py-0.5 rounded border border-purple-500/30">AMIRAL</span>
          </span>
          {showAdminCredentials ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </CardTitle>
      </CardHeader>
      {showAdminCredentials && (
      <CardContent>
        <div className="text-sm text-slate-500 mb-4 space-y-1">
          <p className="text-amber-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <strong>Section réservée aux Amiraux</strong>
          </p>
          <p>Ces identifiants sont utilisés par l&apos;application pour publier les articles.</p>
          <p className="text-xs">Non visibles par les autres utilisateurs.</p>
        </div>

        {sites.length === 0 ? (
          <p className="text-slate-400 text-center py-4">Aucun site configuré</p>
        ) : (
          <div className="space-y-4">
            {sites.map((site) => {
              const adminCred = adminCredentials[site.id] || {};
              const hasAdminCreds = (adminCred.username || '').trim() && (adminCred.password || '').trim();
              return (
                <div key={site.id} className="p-4 bg-slate-900/50 rounded-lg border border-purple-800/50" data-testid={`site-admin-${site.id}`}>
                  <div className="flex items-center mb-3 flex-wrap gap-2">
                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center overflow-hidden">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${site.url}&sz=32`}
                        alt=""
                        className="w-5 h-5"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-purple-400 font-semibold truncate">{site.name}</p>
                      <p className="text-xs text-slate-500 truncate">{site.url}</p>
                    </div>
                    {hasAdminCreds ? (
                      <span className="text-[10px] text-green-400 bg-green-500/10 px-2 py-1 rounded border border-green-500/30">
                        ✓ Configuré
                      </span>
                    ) : (
                      <span className="text-[10px] text-red-400 bg-red-500/10 px-2 py-1 rounded border border-red-500/30">
                        ⚠ Non configuré
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-[10px] text-slate-500">Login WordPress</label>
                      <Input
                        value={adminCred.username || ''}
                        onChange={(e) => onUpdateAdminCredential(site.id, 'username', e.target.value)}
                        placeholder="admin@site.com"
                        className="mt-1 bg-slate-950/50 border-purple-700/50 text-slate-100 text-sm"
                        data-testid={`admin-username-${site.id}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Mot de passe / App Password</label>
                      <div className="relative mt-1">
                        <Input
                          type={showAdminPasswords[site.id] ? 'text' : 'password'}
                          value={adminCred.password || ''}
                          onChange={(e) => onUpdateAdminCredential(site.id, 'password', e.target.value)}
                          placeholder="••••••••"
                          className="bg-slate-950/50 border-purple-700/50 text-slate-100 text-sm pr-10"
                          data-testid={`admin-password-${site.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAdminPasswords(prev => ({ ...prev, [site.id]: !prev[site.id] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showAdminPasswords[site.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => onSaveAdminCredentials(site.id)}
                    disabled={savingAdminCreds[site.id]}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid={`save-admin-${site.id}`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingAdminCreds[site.id] ? 'Sauvegarde...' : 'Enregistrer'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default AdminCredentialsCard;
